import * as React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Checkbox, Divider, Radio, Tooltip } from 'antd';
import { ExportButton } from '@monorepo/shared-lib';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ReportCssWrapper } from 'components/Vat/Report/report-css-wrapper';
import { StyledCard } from 'components/Custom/card';
import {
  isTifdReportSaved,
  TifdLine,
  TifdReport,
  TifdReportContext,
  TifdSection,
  TifdSheetKind,
  TifdValidation,
} from 'store/tifd/models';
import { maltaTifdMappingPath } from 'utils/tifd-mapping';
import { useNumberFormat } from 'utils/numberFormat';

interface TifdReturnFormProps {
  report: TifdReport;
  context: TifdReportContext | null;
  onFillWorkbook: (file: File) => void;
  workbookLoading: boolean;
}

interface DisplaySource {
  accountId: string;
  accountCode: string;
  accountName: string;
  /** Exact amounts keyed the same way as the line amounts. */
  amounts: Map<number, number>;
}

interface DisplayRow {
  key: string;
  code: string;
  label: string;
  hint: string | null;
  type: TifdLine['type'];
  /** Amounts keyed by activity number. Balance sheet uses activity 0 for the single column. */
  amounts: Map<number, number | null>;
  sources: DisplaySource[];
}

const groupSectionRows = (
  section: TifdSection,
  sheet: TifdSheetKind
): DisplayRow[] => {
  const order: string[] = [];
  const rows = new Map<string, DisplayRow>();

  section.lines.forEach((line) => {
    let row = rows.get(line.code);
    if (!row) {
      row = {
        key: `${section.name}-${line.code}`,
        code: line.code,
        label: line.label,
        hint: line.hint,
        type: line.type,
        amounts: new Map(),
        sources: [],
      };
      rows.set(line.code, row);
      order.push(line.code);
    }
    const currentRow = row;
    if (line.type === 'SYSTEM_GENERATED') {
      currentRow.type = 'SYSTEM_GENERATED';
    }
    if (line.hint && !currentRow.hint) {
      currentRow.hint = line.hint;
    }
    const column = sheet === 'BALANCE_SHEET' ? 0 : line.activityNo;
    const current = currentRow.amounts.get(column);
    currentRow.amounts.set(
      column,
      current == null ? line.roundedAmount : current + line.roundedAmount
    );
    (line.sources ?? []).forEach((source) => {
      const accountId = source.accountId || source.accountCode;
      let entry = currentRow.sources.find((item) => item.accountId === accountId);
      if (!entry) {
        entry = {
          accountId,
          accountCode: source.accountCode,
          accountName: source.accountName,
          amounts: new Map(),
        };
        currentRow.sources.push(entry);
      }
      entry.amounts.set(
        column,
        (entry.amounts.get(column) ?? 0) + source.amount
      );
    });
  });

  return order.map((code) => rows.get(code) as DisplayRow);
};

const rowVisible = (row: DisplayRow, showEmptyLines: boolean): boolean => {
  if (showEmptyLines || row.type === 'SYSTEM_GENERATED') {
    return true;
  }
  return Array.from(row.amounts.values()).some(
    (amount) => amount != null && amount !== 0
  );
};

const severityClass = (severity: TifdValidation['severity']): string => {
  if (severity === 'WARNING') return 'scope-tifd-validation--warning';
  if (severity === 'INFO') return 'scope-tifd-validation--info';
  return 'scope-tifd-validation--error';
};

export const TifdReturnForm: React.FC<TifdReturnFormProps> = ({
  report,
  context,
  onFillWorkbook,
  workbookLoading,
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useNumberFormat();
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [sheet, setSheet] = React.useState<TifdSheetKind>('BALANCE_SHEET');
  const [showEmptyLines, setShowEmptyLines] = React.useState(false);
  const [expandedKeys, setExpandedKeys] = React.useState<string[]>([]);
  const [dismissedValidations, setDismissedValidations] = React.useState<
    string[]
  >([]);

  const saved = isTifdReportSaved(report);
  const activeSheet = report.sheets.find((item) => item.sheet === sheet);
  const activities = [...(report.activities ?? [])].sort(
    (a, b) => a.activityNo - b.activityNo
  );
  const amountColumns =
    sheet === 'INCOME'
      ? activities.map((activity) => ({
          key: activity.activityNo,
          label: activity.establishmentName,
        }))
      : [{ key: 0, label: t('tifd-report.dr-cr') }];

  const wholeNumber = (value: number | null | undefined): string => {
    if (value == null) return '';
    return formatNumber(value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const exactNumber = (value: number): string =>
    formatNumber(value, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const exactColumnTotal = (row: DisplayRow, column: number): number | null => {
    let seen = false;
    let total = 0;
    row.sources.forEach((source) => {
      const amount = source.amounts.get(column);
      if (amount == null) return;
      seen = true;
      total += amount;
    });
    return seen ? total : null;
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );
  };

  const mappingPath =
    context?.connectionId && context.mappingId
      ? maltaTifdMappingPath(context.connectionId, context.mappingId)
      : null;

  const validationKey = (validation: TifdValidation, index: number): string =>
    `${validation.severity}:${validation.rule}:${index}`;

  React.useEffect(() => {
    setDismissedValidations([]);
  }, [report.generatedDate, report.validations]);

  return (
    <ReportCssWrapper>
      <StyledCard
        style={{
          maxWidth: 1150,
          minWidth: 780,
          margin: 'auto',
          width: '100%',
        }}
      >
      <div className="scope-tifd-form">
      <div className="scope-tifd-toolbar">
        <Tooltip
          title={saved ? undefined : t('tifd-report.workbook-after-save')}
        >
          <span>
            <ExportButton
              disabled={!saved || workbookLoading}
              loading={workbookLoading}
              onClick={() => {
                const picker = fileRef.current as
                  | (HTMLInputElement & { showPicker?: () => void })
                  | null;
                if (!picker) return;
                try {
                  if (typeof picker.showPicker === 'function') {
                    picker.showPicker();
                    return;
                  }
                } catch {
                  // The browser blocked showPicker. The click below still opens the dialog.
                }
                picker.click();
              }}
            >
              <span>{t('tifd-report.fill-workbook')}</span>
            </ExportButton>
          </span>
        </Tooltip>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xlsm"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) onFillWorkbook(file);
          }}
        />
        <div className="scope-tifd-toolbar__controls">
          <Radio.Group
            value={sheet}
            buttonStyle="solid"
            style={{ borderRadius: '10px' }}
            onChange={(event) => {
              setSheet(event.target.value as TifdSheetKind);
              setExpandedKeys([]);
            }}
          >
            <Radio.Button value="BALANCE_SHEET">
              {t('returns.mapping.sheet-balance-sheet')}
            </Radio.Button>
            <Radio.Button value="INCOME">
              {t('returns.mapping.sheet-income')}
            </Radio.Button>
          </Radio.Group>
          <Checkbox
            checked={showEmptyLines}
            onChange={(event) => setShowEmptyLines(event.target.checked)}
          >
            {t('tifd-report.lines-with-no-amount')}
          </Checkbox>
        </div>
      </div>
      <Divider />

      {(report.validations ?? []).map((validation, index) => {
        const key = validationKey(validation, index);
        if (dismissedValidations.includes(key)) return null;
        return (
          <div
            key={key}
            className={`scope-tifd-validation ${severityClass(validation.severity)}`}
          >
            <div className="scope-tifd-validation__body">
              <div>
                <span className="scope-tifd-validation__severity">
                  {validation.severity}
                </span>
                <span className="scope-tifd-validation__rule">
                  {validation.rule}
                </span>
                <span>{validation.message}</span>
              </div>
              {validation.details?.length > 0 && (
                <ul>
                  {validation.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              )}
              {validation.rule === 'UNMAPPED_ACCOUNTS' && mappingPath && (
                <Link to={mappingPath}>{t('tifd-report.go-to-mapping')}</Link>
              )}
            </div>
            <button
              type="button"
              className="scope-tifd-validation__close"
              aria-label={t('common.close')}
              onClick={() =>
                setDismissedValidations((current) =>
                  current.includes(key) ? current : [...current, key]
                )
              }
            >
              <CloseOutlined />
            </button>
          </div>
        );
      })}

      {(activeSheet?.sections ?? []).map((section) => {
        const rows = groupSectionRows(section, sheet).filter((row) =>
          rowVisible(row, showEmptyLines)
        );
        if (rows.length === 0) return null;
        return (
          <table
            key={section.name}
            className="vatreporttable table scope-tifd-table"
          >
            <colgroup>
              <col className="scope-tifd-col-label" />
              <col className="scope-tifd-col-code" />
              {amountColumns.map((column) => (
                <col key={column.key} className="scope-tifd-col-amount" />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th colSpan={2}>{section.name}</th>
                {amountColumns.map((column) => (
                  <th key={column.key} className="scope-tifd-amount">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const canExpand =
                  row.type === 'USER_DEFINED' && row.sources.length > 0;
                const open = expandedKeys.includes(row.key);
                return (
                  <React.Fragment key={row.key}>
                    <tr
                      className={canExpand ? 'scope-tifd-row--expandable' : undefined}
                      aria-expanded={canExpand ? open : undefined}
                      onClick={() => {
                        if (!canExpand) return;
                        toggleExpanded(row.key);
                      }}
                      onKeyDown={(event) => {
                        if (!canExpand) return;
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        toggleExpanded(row.key);
                      }}
                      tabIndex={canExpand ? 0 : undefined}
                    >
                      <td
                        className={
                          row.type === 'SYSTEM_GENERATED'
                            ? 'scope-tifd-label subtotal'
                            : 'scope-tifd-label'
                        }
                      >
                        {row.hint ? (
                          <Tooltip title={row.hint}>
                            <span>{row.label}</span>
                          </Tooltip>
                        ) : (
                          row.label
                        )}
                      </td>
                      <td className="box">{row.code}</td>
                      {amountColumns.map((column) => (
                        <td key={column.key} className="scope-tifd-amount">
                          {wholeNumber(row.amounts.get(column.key) ?? null)}
                        </td>
                      ))}
                    </tr>
                    {open &&
                      row.sources.map((source) => (
                        <tr
                          key={`${row.key}-${source.accountId}`}
                          className="scope-tifd-sources"
                        >
                          <td className="scope-tifd-label">
                            {source.accountCode} {source.accountName}
                          </td>
                          <td />
                          {amountColumns.map((column) => (
                            <td key={column.key} className="scope-tifd-amount">
                              {source.amounts.has(column.key)
                                ? exactNumber(
                                    source.amounts.get(column.key) as number
                                  )
                                : ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    {open && (
                      <tr className="scope-tifd-sources scope-tifd-source-total">
                        <td className="scope-tifd-label">
                          {t('tifd-report.exact-total')}
                        </td>
                        <td />
                        {amountColumns.map((column) => {
                          const total = exactColumnTotal(row, column.key);
                          return (
                            <td key={column.key} className="scope-tifd-amount">
                              {total == null ? '' : exactNumber(total)}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        );
      })}

      <p className="scope-tifd-note">{t('tifd-report.footnote')}</p>
      </div>
      </StyledCard>
    </ReportCssWrapper>
  );
};
